import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import api, { BACKEND_URL, getAccessToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Send, Hash, CornerUpLeft, X, ChevronLeft, Search, ImagePlus, Pin, Images, Undo2 } from "lucide-react";
import { toast } from "sonner";
import EmojiPicker from "@/components/EmojiPicker";
import ReactionRow from "@/components/ReactionRow";

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

/** Initials from a display name (max 2 chars). */
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

/** Soft colour for avatar backgrounds based on user id/name. */
function avatarColor(str) {
  const palette = [
    "bg-blue-500", "bg-emerald-500", "bg-violet-500", "bg-amber-500",
    "bg-rose-500", "bg-cyan-500", "bg-fuchsia-500", "bg-lime-600",
    "bg-indigo-500", "bg-orange-500",
  ];
  let h = 0;
  for (let i = 0; i < (str || "").length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length];
}

/** Format a timestamp to a short time label. */
function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

/** Format a date for the date separator between message groups. */
function fmtDateSeparator(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
    const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    if (d.toDateString() === today.toDateString()) return time;
    if (d.toDateString() === yesterday.toDateString()) return `Yesterday ${time}`;
    return d.toLocaleDateString(undefined, { weekday: "short" }) + " " + time;
  } catch { return ""; }
}

/**
 * Group consecutive messages from the same sender.
 * Each group = { sender_id, sender_name, messages: [...], isSystem }.
 * System messages break groups and get their own entry.
 */
function groupMessages(messages) {
  const groups = [];
  for (const m of messages) {
    if (m.type === "system") {
      groups.push({ isSystem: true, messages: [m] });
      continue;
    }
    const last = groups[groups.length - 1];
    if (last && !last.isSystem && last.sender_id === m.sender_id) {
      last.messages.push(m);
    } else {
      groups.push({ isSystem: false, sender_id: m.sender_id, sender_name: m.sender_name, messages: [m] });
    }
  }
  return groups;
}

/* ------------------------------------------------------------------ */
/*  Room List Item                                                    */
/* ------------------------------------------------------------------ */

function RoomItem({ room, active, unread, onClick, onlineUsers, currentUserId }) {
  // Compute time-ago for the latest message
  const timeAgo = useMemo(() => {
    const ts = room.last_message_at || room.updated_at;
    if (!ts) return "";
    try {
      const diff = Date.now() - new Date(ts).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return "now";
      if (mins < 60) return `${mins}m`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h`;
      const days = Math.floor(hrs / 24);
      if (days < 7) return `${days}d`;
      return `${Math.floor(days / 7)}w`;
    } catch { return ""; }
  }, [room.last_message_at, room.updated_at]);

  // Check if any member (besides self) in this room is online
  const hasOnlineMember = useMemo(() => {
    if (!room.member_ids || !onlineUsers) return false;
    return room.member_ids.some((uid) => uid !== currentUserId && onlineUsers.has(uid));
  }, [room.member_ids, onlineUsers, currentUserId]);

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all group/room ${
        active
          ? "bg-[hsl(var(--accent))]/10"
          : "hover:bg-muted/40"
      }`}
      data-testid={`chat-room-${room.id}`}
    >
      <div className="relative shrink-0">
        <Avatar className="h-14 w-14">
          <AvatarFallback className="bg-gradient-to-br from-[hsl(var(--accent))] to-[hsl(var(--accent))]/70 text-white text-base font-bold">
            {room.name?.[0]?.toUpperCase() || "#"}
          </AvatarFallback>
        </Avatar>
        {hasOnlineMember && (
          <span
            className="absolute bottom-0.5 right-0.5 h-3.5 w-3.5 rounded-full bg-emerald-500 border-[2.5px] border-background"
            data-testid={`presence-dot-room-${room.id}`}
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className={`truncate text-[15px] leading-tight ${unread ? "font-bold text-foreground" : active ? "font-semibold text-foreground" : "font-medium text-foreground"}`}>
          {room.name}
        </div>
        <div className="flex items-center gap-0 mt-1">
          <span className={`truncate text-[13px] ${unread ? "text-foreground font-medium" : "text-muted-foreground"}`}>
            {room.last_message_text || `${room.member_ids?.length || 0} members`}
          </span>
          {timeAgo && (
            <span className={`shrink-0 text-[13px] ${unread ? "text-foreground font-medium" : "text-muted-foreground"}`}>
              {" · "}{timeAgo}
            </span>
          )}
        </div>
      </div>
      {unread && (
        <span className="h-3 w-3 rounded-full bg-[hsl(var(--accent))] shrink-0" />
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Message Bubble                                                    */
/* ------------------------------------------------------------------ */

function MessageBubble({
  msg, mine, isFirst, isLast, userId, onReply, onReaction, onPin, onUnsend,
  msgRefs, scrollToMessage, highlightId, seenUsers, isPinned, isAdmin,
}) {
  const reactions = msg.reactions || [];
  const isHighlighted = highlightId && highlightId === msg.id;

  return (
    <div
      ref={(node) => { if (msg.id && node) msgRefs.current[msg.id] = node; }}
      className={`flex ${mine ? "justify-end" : "justify-start"} group ${
        isFirst ? "mt-3" : "mt-0.5"
      }`}
      data-testid={`chat-message-${msg.id}`}
    >
      {/* Avatar column — only for received messages, only on last bubble in group */}
      {!mine && (
        <div className="w-8 shrink-0 flex items-end mr-2">
          {isLast && (
            <Avatar className="h-7 w-7">
              <AvatarFallback className={`${avatarColor(msg.sender_id)} text-white text-[10px] font-bold`}>
                {initials(msg.sender_name)}
              </AvatarFallback>
            </Avatar>
          )}
        </div>
      )}

      <div className={`max-w-[72%] flex flex-col ${mine ? "items-end" : "items-start"} gap-0.5`}>
        {/* Sender name — only on first message in group for received */}
        {!mine && isFirst && (
          <span className="text-[11px] font-semibold text-muted-foreground ml-1 mb-0.5">{msg.sender_name}</span>
        )}

        {/* Quoted parent */}
        {msg.parent_id && (
          <button
            type="button"
            onClick={() => scrollToMessage(msg.parent_id)}
            className={`flex items-stretch gap-0 max-w-full text-left rounded-xl overflow-hidden border border-border/50 bg-muted/50 hover:bg-muted transition-colors mb-0.5 ${mine ? "self-end" : "self-start"}`}
            data-testid={`chat-message-${msg.id}-parent`}
            title="Jump to the original message"
          >
            <span className="w-1 shrink-0 bg-[hsl(var(--accent))]" />
            <span className="px-2.5 py-1.5 min-w-0">
              <span className="block text-[10px] font-semibold text-[hsl(var(--accent))] truncate">
                {msg.parent_sender_name || "Member"}
              </span>
              <span className="block text-[11px] text-muted-foreground line-clamp-1 break-words">
                {msg.parent_text || "(message)"}
              </span>
            </span>
          </button>
        )}

        {/* The bubble */}
        <div
          className={`relative overflow-hidden text-[14px] leading-relaxed transition-all ${
            mine
              ? `text-white ${isLast ? "rounded-[20px_20px_4px_20px]" : isFirst ? "rounded-[20px_20px_20px_4px]" : "rounded-[20px_4px_4px_20px]"}`
              : `text-foreground ${isLast ? "rounded-[20px_20px_20px_4px]" : isFirst ? "rounded-[4px_20px_20px_20px]" : "rounded-[4px_20px_20px_4px]"}`
          } ${isHighlighted ? "ring-2 ring-[hsl(var(--accent))]/60 ring-offset-1 ring-offset-background" : ""} ${
            msg.image_url && !msg.text ? "p-1" : "px-3.5 py-2"
          }`}
          style={
            mine
              ? { background: "linear-gradient(135deg, #7646FF 0%, #a259ff 100%)" }
              : { background: "hsl(var(--muted))" }
          }
        >
          {msg.image_url && (
            <img
              src={msg.image_url}
              alt="Shared image"
              className={`max-w-[260px] sm:max-w-[320px] max-h-[300px] w-auto h-auto object-contain rounded-[16px] cursor-pointer hover:opacity-90 transition-opacity ${msg.text ? "mb-1.5" : ""}`}
              loading="lazy"
              onClick={() => window.open(msg.image_url, "_blank")}
              data-testid={`chat-image-${msg.id}`}
            />
          )}
          {msg.text && <div className="whitespace-pre-wrap break-words text-base sm:text-sm">{msg.text}</div>}
        </div>

        {/* Time — only on last bubble */}
        {isLast && (
          <span className={`text-[10px] text-muted-foreground/60 px-1 mt-0.5 ${mine ? "text-right" : "text-left"}`}>
            {fmtTime(msg.created_at)}
          </span>
        )}

        {/* Action row: reactions + reply + pin */}
        {msg.id && (
          <div className="relative">
            {/* Floating reaction badges at bubble bottom — always visible if reactions exist */}
            {reactions.length > 0 && (
              <div className={`flex items-center gap-0.5 -mt-2 mb-1 ${mine ? "justify-end mr-2" : "justify-start ml-2"}`}>
                <div className="inline-flex items-center gap-0 bg-background border border-border rounded-full px-1.5 py-0.5 shadow-sm">
                  <ReactionRow
                    reactions={reactions}
                    currentUserId={userId}
                    onToggle={(emoji) => onReaction(msg.id, emoji)}
                    size="xs"
                    testid={`chat-reactions-${msg.id}`}
                  />
                </div>
              </div>
            )}
            {/* Hover-only action buttons */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
              {reactions.length === 0 && (
                <ReactionRow
                  reactions={reactions}
                  currentUserId={userId}
                  onToggle={(emoji) => onReaction(msg.id, emoji)}
                  size="sm"
                  testid={`chat-reactions-${msg.id}`}
                />
              )}
              <button
                type="button"
                onClick={() => onReply(msg)}
                className="inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                aria-label={`Reply to ${msg.sender_name}`}
                data-testid={`chat-reply-btn-${msg.id}`}
              >
                <CornerUpLeft className="h-3 w-3" />
                Reply
              </button>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => onPin(msg.id, isPinned)}
                  className={`inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] transition-colors ${
                    isPinned
                      ? "text-[hsl(var(--accent))] hover:text-foreground hover:bg-muted"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`}
                  aria-label={isPinned ? "Unpin message" : "Pin message"}
                  data-testid={`chat-pin-btn-${msg.id}`}
                >
                  <Pin className={`h-3 w-3 ${isPinned ? "fill-current" : ""}`} />
                  {isPinned ? "Unpin" : "Pin"}
                </button>
              )}
              {(mine || isAdmin) && (
                <button
                  type="button"
                  onClick={() => onUnsend(msg.id)}
                  className="inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  aria-label="Unsend message"
                  data-testid={`chat-unsend-btn-${msg.id}`}
                >
                  <Undo2 className="h-3 w-3" />
                  Unsend
                </button>
              )}
            </div>
          </div>
        )}

        {/* Read receipt avatars — Messenger style, only on own messages */}
        {mine && seenUsers && seenUsers.length > 0 && (
          <div className="flex items-center gap-0.5 mt-0.5 justify-end" data-testid={`chat-seen-${msg.id}`}>
            {seenUsers.slice(0, 5).map((su) => (
              <Avatar key={su.user_id} className="h-4 w-4" title={`Seen by ${su.user_name}`}>
                <AvatarFallback className={`${avatarColor(su.user_id)} text-white text-[7px] font-bold`}>
                  {initials(su.user_name)}
                </AvatarFallback>
              </Avatar>
            ))}
            {seenUsers.length > 5 && (
              <span className="text-[9px] text-muted-foreground/60 ml-0.5">+{seenUsers.length - 5}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                    */
/* ------------------------------------------------------------------ */

export default function MemberChat() {
  const { roomId } = useParams();
  const nav = useNavigate();
  const { user, isAdmin } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [activeId, setActiveId] = useState(roomId || null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [pendingImage, setPendingImage] = useState(null); // { dataUrl, file }
  const [highlightId, setHighlightId] = useState(null);
  const [unreadRoomIds, setUnreadRoomIds] = useState(new Set());
  const [roomSearch, setRoomSearch] = useState("");
  const [roomFilter, setRoomFilter] = useState("all"); // "all" | "unread" | "groups"
  const [msgSearch, setMsgSearch] = useState("");
  const [msgSearchResults, setMsgSearchResults] = useState(null); // null = closed, [] = no results
  const [msgSearchBusy, setMsgSearchBusy] = useState(false);
  const [typingUsers, setTypingUsers] = useState({}); // { senderId: { name, timer } }
  const [readReceipts, setReadReceipts] = useState([]); // [{ user_id, user_name, last_message_id }]
  const [pinnedMessages, setPinnedMessages] = useState([]); // pinned messages for active room
  const [showPinnedPanel, setShowPinnedPanel] = useState(false);
  const [mediaGallery, setMediaGallery] = useState(null); // null = closed, [] = no media
  const [mediaLoading, setMediaLoading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null); // full-screen image preview
  const [onlineUsers, setOnlineUsers] = useState(new Set()); // set of online user IDs
  const wsRef = useRef(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const msgRefs = useRef({});
  const typingTimerRef = useRef(null);
  const fileInputRef = useRef(null); // debounce outgoing typing events
  const refreshUnread = async () => {
    try {
      const { data } = await api.get("/chat/unread");
      setUnreadRoomIds(new Set(data?.room_ids || []));
    } catch (_) { console.warn("[Chat] Failed to refresh unread", _); }
  };

  useEffect(() => {
    api.get("/chat/rooms").then(({ data }) => {
      setRooms(data || []);
      if (!activeId && data && data[0]) setActiveId(data[0].id);
    });
    refreshUnread();
    // Fetch initial online presence
    api.get("/chat/presence").then(({ data }) => {
      setOnlineUsers(new Set(data || []));
    }).catch((err) => { console.warn("[Chat] Presence fetch failed", err); });
    const t = setInterval(refreshUnread, 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { if (roomId) setActiveId(roomId); }, [roomId]);

  useEffect(() => {
    if (!activeId) return;
    setReplyTo(null);
    if (unreadRoomIds.has(activeId)) {
      api.post(`/chat/ack/${activeId}`).then(() => {
        setUnreadRoomIds((prev) => { const s = new Set(prev); s.delete(activeId); return s; });
      }).catch((err) => { console.warn("[Chat] Ack failed", err); });
    }
  }, [activeId, unreadRoomIds]);

  useEffect(() => {
    const currentToken = getAccessToken();
    if (!activeId || !currentToken) return;
    setTypingUsers({});
    setReadReceipts([]);
    setPinnedMessages([]);
    setShowPinnedPanel(false);
    setMediaGallery(null);
    setLightboxUrl(null);
    api.get(`/chat/rooms/${activeId}/messages`).then(({ data }) => setMessages(data || [])).catch(() => setMessages([]));
    api.get(`/chat/rooms/${activeId}/seen`).then(({ data }) => setReadReceipts(data || [])).catch((err) => { console.warn("[Chat] Seen fetch failed", err); });
    api.get(`/chat/rooms/${activeId}/pinned`).then(({ data }) => setPinnedMessages(data || [])).catch((err) => { console.warn("[Chat] Pinned fetch failed", err); });
    const wsBase = BACKEND_URL.replace(/^http/, "ws");
    const ws = new WebSocket(`${wsBase}/api/ws/chat/${activeId}?token=${encodeURIComponent(currentToken)}`);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "message") {
          setMessages((prev) => [...prev, { ...data, reactions: data.reactions || [] }]);
          // Clear typing indicator for this sender since they sent a message
          if (data.sender_id) {
            setTypingUsers((prev) => { const n = { ...prev }; delete n[data.sender_id]; return n; });
          }
          if (data.room_id && data.room_id !== activeId && data.sender_id !== user?.id) {
            setUnreadRoomIds((prev) => new Set(prev).add(data.room_id));
            toast.message(`${data.sender_name}: ${data.text.slice(0, 80)}`, { description: rooms.find((r) => r.id === data.room_id)?.name || "New message" });
          }
        }
        if (data.type === "system") setMessages((prev) => [...prev, data]);
        if (data.type === "reaction" && data.message_id) {
          setMessages((prev) => prev.map((m) => (m.id === data.message_id ? { ...m, reactions: data.reactions || [] } : m)));
        }
        if (data.type === "typing" && data.sender_id && data.sender_id !== user?.id) {
          setTypingUsers((prev) => {
            // Clear previous timeout for this sender
            if (prev[data.sender_id]?.timer) clearTimeout(prev[data.sender_id].timer);
            // Auto-clear after 3s of no typing events
            const timer = setTimeout(() => {
              setTypingUsers((p) => { const n = { ...p }; delete n[data.sender_id]; return n; });
            }, 3000);
            return { ...prev, [data.sender_id]: { name: data.sender_name, timer } };
          });
        }
        if (data.type === "seen" && data.user_id && data.user_id !== user?.id) {
          setReadReceipts((prev) => {
            const filtered = prev.filter((r) => r.user_id !== data.user_id);
            return [...filtered, { user_id: data.user_id, user_name: data.user_name, last_message_id: data.last_message_id }];
          });
        }
        if (data.type === "pin") {
          if (data.action === "added" && data.message) {
            setPinnedMessages((prev) => [
              { ...data.message, pinned_at: data.pinned_at, pinned_by_name: data.pinned_by_name },
              ...prev.filter((p) => p.id !== data.message_id),
            ]);
          }
          if (data.action === "removed") {
            setPinnedMessages((prev) => prev.filter((p) => p.id !== data.message_id));
          }
        }
        if (data.type === "presence") {
          setOnlineUsers((prev) => {
            const next = new Set(prev);
            if (data.status === "online") next.add(data.user_id);
            else next.delete(data.user_id);
            return next;
          });
        }
        if (data.type === "unsend" && data.message_id) {
          setMessages((prev) => prev.filter((m) => m.id !== data.message_id));
          setPinnedMessages((prev) => prev.filter((p) => p.id !== data.message_id));
        }
      } catch (err) { console.warn("[Chat] WS message parse error", err); }
    };
    ws.onerror = () => toast.error("Chat connection error");
    ws.onclose = (e) => { if (e.code === 4403) { toast.error("You're not a member of this room"); } };
    return () => {
      // Cleanup typing timers
      setTypingUsers((prev) => { Object.values(prev).forEach((v) => clearTimeout(v.timer)); return {}; });
      try { ws.close(); } catch (err) { console.warn("[Chat] WS close error", err); }
    };
  }, [activeId]); // getAccessToken is a stable module export; BACKEND_URL is a constant

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  /** Emit a "seen" receipt for the last message in view. */
  const emitSeen = (msgId) => {
    if (!msgId || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ type: "seen", message_id: msgId }));
  };

  // When messages update and we have focus, mark the last one as seen
  useEffect(() => {
    if (!messages.length || !activeId) return;
    const lastMsg = [...messages].reverse().find((m) => m.id && m.type !== "system");
    if (lastMsg?.id && lastMsg.sender_id !== user?.id) {
      // Small delay to let the scroll finish
      const t = setTimeout(() => emitSeen(lastMsg.id), 500);
      return () => clearTimeout(t);
    }
  }, [messages, activeId]);

  /** Send a "typing" event via WS — debounced to max once per 2s. */
  const emitTyping = () => {
    if (!wsRef.current || wsRef.current.readyState !== 1) return;
    if (typingTimerRef.current) return; // already sent recently
    wsRef.current.send(JSON.stringify({ type: "typing" }));
    typingTimerRef.current = setTimeout(() => { typingTimerRef.current = null; }, 2000);
  };

  const send = () => {
    const t = text.trim();
    if ((!t && !pendingImage) || pendingImage?.compressing || !wsRef.current || wsRef.current.readyState !== 1) return;
    const payload = { text: t };
    if (replyTo?.id) payload.parent_id = replyTo.id;
    if (pendingImage?.dataUrl) payload.image_url = pendingImage.dataUrl;
    wsRef.current.send(JSON.stringify(payload));
    setText("");
    setReplyTo(null);
    setPendingImage(null);
  };

  const appendEmoji = (emoji) => {
    setText((prev) => prev + emoji);
    setTimeout(() => { try { inputRef.current?.focus(); } catch (e) { console.debug("[Chat] focus after emoji failed:", e?.message); } }, 0);
  };

  const toggleReaction = async (messageId, emoji) => {
    try {
      await api.post(`/chat/messages/${messageId}/reactions`, { emoji });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not react");
    }
  };

  const pinMessage = async (messageId) => {
    if (!activeId) return;
    try {
      await api.post(`/chat/rooms/${activeId}/pin/${messageId}`);
      toast.success("Message pinned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pin message");
    }
  };

  const unpinMessage = async (messageId) => {
    if (!activeId) return;
    try {
      await api.delete(`/chat/rooms/${activeId}/pin/${messageId}`);
      toast.success("Message unpinned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not unpin message");
    }
  };

  const unsendMessage = async (messageId) => {
    try {
      await api.delete(`/chat/messages/${messageId}`);
      toast.success("Message unsent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not unsend message");
    }
  };

  /** Set of pinned message IDs for quick lookup */
  const pinnedIds = useMemo(() => new Set(pinnedMessages.map((p) => p.id)), [pinnedMessages]);

  /** Open the media gallery for the current room. */
  const openMediaGallery = async () => {
    if (!activeId) return;
    setMediaGallery([]);
    setMediaLoading(true);
    try {
      const { data } = await api.get(`/chat/rooms/${activeId}/media`);
      setMediaGallery(data || []);
    } catch (_) {
      toast.error("Could not load media");
      setMediaGallery(null);
    } finally {
      setMediaLoading(false);
    }
  };

  const startReply = (m) => {
    setReplyTo({ id: m.id, sender_name: m.sender_name, sender_id: m.sender_id, text: (m.text || "").slice(0, 200) });
    setTimeout(() => { try { inputRef.current?.focus(); } catch (e) { console.debug("[Chat] focus after reply failed:", e?.message); } }, 0);
  };

  /**
   * Compress an image file using canvas.
   * - Caps longest edge at MAX_DIM px
   * - Outputs JPEG at TARGET_QUALITY
   * - Returns a data URL string
   */
  const compressImage = (file) => new Promise((resolve, reject) => {
    const MAX_DIM = 1200;        // max width or height in px
    const TARGET_QUALITY = 0.70; // JPEG quality 0-1
    const img = new Image();
    img.onload = () => {
      try {
        let { width, height } = img;
        // Scale down if either dimension exceeds MAX_DIM
        if (width > MAX_DIM || height > MAX_DIM) {
          const ratio = Math.min(MAX_DIM / width, MAX_DIM / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", TARGET_QUALITY);
        URL.revokeObjectURL(img.src);
        resolve(dataUrl);
      } catch (err) {
        URL.revokeObjectURL(img.src);
        reject(err);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(img.src); reject(new Error("Image load failed")); };
    img.src = URL.createObjectURL(file);
  });

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.type !== "image/png" && !file.name.toLowerCase().endsWith(".png")) { toast.error("Only PNG images are allowed."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10 MB"); return; }
    e.target.value = ""; // reset so the same file can be re-selected
    setPendingImage({ dataUrl: null, name: file.name, compressing: true });
    compressImage(file).then((dataUrl) => {
      setPendingImage({ dataUrl, name: file.name, compressing: false });
    }).catch(() => {
      // Fallback: use original if compression fails
      const reader = new FileReader();
      reader.onload = () => setPendingImage({ dataUrl: reader.result, name: file.name, compressing: false });
      reader.readAsDataURL(file);
    });
  };

  const scrollToMessage = (id) => {
    const node = msgRefs.current[id];
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightId(id);
    setTimeout(() => setHighlightId((h) => (h === id ? null : h)), 1600);
  };

  /** Search messages across rooms by keyword. */
  const performMsgSearch = async (query) => {
    const q = (query || "").trim();
    if (q.length < 2) { setMsgSearchResults([]); return; }
    setMsgSearchBusy(true);
    try {
      const { data } = await api.get("/chat/search", { params: { q } });
      setMsgSearchResults(data || []);
    } catch (_) {
      toast.error("Search failed");
      setMsgSearchResults([]);
    } finally {
      setMsgSearchBusy(false);
    }
  };

  /** Handle clicking a search result: navigate to room and highlight message. */
  const handleSearchResultClick = (result) => {
    // Close search overlay
    setMsgSearch("");
    setMsgSearchResults(null);
    // Navigate to the room if different
    if (result.room_id !== activeId) {
      setActiveId(result.room_id);
      nav(`/m/chat/${result.room_id}`);
      // After room loads, try to scroll to message (small delay for load)
      setTimeout(() => scrollToMessage(result.id), 800);
    } else {
      scrollToMessage(result.id);
    }
  };

  const activeRoom = rooms.find((r) => r.id === activeId);
  const messageGroups = useMemo(() => groupMessages(messages), [messages]);

  // Build typing indicator text from typingUsers
  const typingNames = useMemo(() => {
    const names = Object.values(typingUsers).map((v) => v.name).filter(Boolean);
    if (names.length === 0) return null;
    if (names.length === 1) return `${names[0]} is typing`;
    if (names.length === 2) return `${names[0]} and ${names[1]} are typing`;
    return `${names[0]} and ${names.length - 1} others are typing`;
  }, [typingUsers]);

  // Map message_id → array of { user_id, user_name } who have seen UP TO that message
  // Only show receipts from OTHER users (not self)
  const seenByMessage = useMemo(() => {
    const map = {};
    for (const r of readReceipts) {
      if (r.user_id === user?.id) continue;
      const mid = r.last_message_id;
      if (!mid) continue;
      if (!map[mid]) map[mid] = [];
      map[mid].push({ user_id: r.user_id, user_name: r.user_name });
    }
    return map;
  }, [readReceipts, user?.id]);

  const filteredRooms = useMemo(() => {
    let list = rooms;
    // Apply tab filter
    if (roomFilter === "unread") {
      list = list.filter((r) => unreadRoomIds.has(r.id));
    } else if (roomFilter === "groups") {
      list = list.filter((r) => (r.member_ids?.length || 0) > 2);
    }
    // Apply search
    if (roomSearch.trim()) {
      const q = roomSearch.toLowerCase();
      list = list.filter((r) => (r.name || "").toLowerCase().includes(q));
    }
    return list;
  }, [rooms, roomSearch, roomFilter, unreadRoomIds]);

  return (
    <AppShell variant="member" title="Chat">
      <div className="lg:grid lg:grid-cols-[320px_1fr] lg:h-[calc(100vh-140px)] lg:min-h-[480px] rounded-2xl overflow-hidden border border-border bg-background shadow-sm">

        {/* ======================== SIDEBAR ======================== */}
        <div
          className={`border-r border-border flex flex-col bg-background ${
            activeId ? "hidden lg:flex" : "flex"
          }`}
          data-testid="chat-room-list"
        >
          {/* Sidebar header */}
          <div className="px-4 pt-4 pb-2">
            <h2 className="text-2xl font-extrabold tracking-tight">Chats</h2>
            {/* Search */}
            <div className="relative mt-2.5">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={roomSearch}
                onChange={(e) => setRoomSearch(e.target.value)}
                placeholder="Search Messenger"
                className="h-9 pl-9 rounded-full bg-muted/50 border-0 text-sm placeholder:text-muted-foreground/60"
                data-testid="chat-room-search"
              />
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex items-center gap-1.5 px-4 pb-2" data-testid="chat-filter-tabs">
            {[
              { key: "all", label: "All" },
              { key: "unread", label: "Unread" },
              { key: "groups", label: "Groups" },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setRoomFilter(tab.key)}
                className={`px-3.5 py-1.5 rounded-full text-[13px] font-medium transition-colors ${
                  roomFilter === tab.key
                    ? "bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]"
                    : "text-muted-foreground hover:bg-muted/50"
                }`}
                data-testid={`chat-filter-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Room list */}
          <div className="flex-1 overflow-y-auto px-2 pb-3">
            {filteredRooms.length === 0 && (
              <div className="text-sm text-muted-foreground p-4 text-center">
                {rooms.length === 0 ? "No rooms yet" : "No matches"}
              </div>
            )}
            {filteredRooms.map((r) => (
              <RoomItem
                key={r.id}
                room={r}
                active={activeId === r.id}
                unread={unreadRoomIds.has(r.id) && activeId !== r.id}
                onClick={() => { setActiveId(r.id); nav(`/m/chat/${r.id}`); }}
                onlineUsers={onlineUsers}
                currentUserId={user?.id}
              />
            ))}
          </div>
        </div>

        {/* ======================== CONVERSATION ======================== */}
        <div
          className={`flex flex-col ${
            activeId ? "flex" : "hidden lg:flex"
          } h-[calc(100vh-140px)] min-h-0 overflow-hidden bg-background`}
        >
          {/* Conversation header */}
          {activeRoom ? (
            <div className="shrink-0 border-b border-border px-4 py-3 flex items-center gap-3 bg-background/80 backdrop-blur-sm">
              <button
                type="button"
                onClick={() => { setActiveId(null); nav("/m/chat"); }}
                className="lg:hidden -ml-1 h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-muted text-foreground/80 hover:text-foreground transition-colors"
                aria-label="Back to rooms"
                data-testid="chat-back-to-rooms"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <div className="relative shrink-0">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-gradient-to-br from-[hsl(var(--accent))] to-[hsl(var(--accent))]/70 text-white text-sm font-bold">
                    {activeRoom.name?.[0]?.toUpperCase() || "#"}
                  </AvatarFallback>
                </Avatar>
                {activeRoom.member_ids?.some((uid) => uid !== user?.id && onlineUsers.has(uid)) && (
                  <span
                    className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-emerald-500 border-2 border-background"
                    data-testid="presence-dot-header"
                  />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold tracking-tight truncate text-[15px]">{activeRoom.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {activeRoom.member_ids?.some((uid) => uid !== user?.id && onlineUsers.has(uid))
                    ? "Active now"
                    : `${activeRoom.member_ids?.length || 0} members`}
                </div>
              </div>
              <button
                type="button"
                onClick={openMediaGallery}
                className={`shrink-0 h-9 w-9 inline-flex items-center justify-center rounded-full transition-colors ${
                  mediaGallery !== null
                    ? "bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]"
                    : "text-foreground/60 hover:text-foreground hover:bg-muted"
                }`}
                aria-label="Shared media"
                data-testid="chat-media-toggle"
              >
                <Images className="h-4.5 w-4.5" />
              </button>
              <button
                type="button"
                onClick={() => setMsgSearchResults(msgSearchResults === null ? [] : null)}
                className={`shrink-0 h-9 w-9 inline-flex items-center justify-center rounded-full transition-colors ${
                  msgSearchResults !== null
                    ? "bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]"
                    : "text-foreground/60 hover:text-foreground hover:bg-muted"
                }`}
                aria-label="Search messages"
                data-testid="chat-search-toggle"
              >
                <Search className="h-4.5 w-4.5" />
              </button>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
              Select a conversation to start chatting
            </div>
          )}

          {/* Messages area */}
          {activeRoom && (
            <>
              {/* Search overlay */}
              {msgSearchResults !== null && (
                <div className="shrink-0 border-b border-border bg-background/95 backdrop-blur-sm" data-testid="chat-search-panel">
                  <div className="px-4 py-3 flex items-center gap-2">
                    <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                    <Input
                      value={msgSearch}
                      onChange={(e) => {
                        setMsgSearch(e.target.value);
                        // Debounced search
                        clearTimeout(window.__chatSearchTimer);
                        window.__chatSearchTimer = setTimeout(() => performMsgSearch(e.target.value), 400);
                      }}
                      onKeyDown={(e) => { if (e.key === "Escape") { setMsgSearch(""); setMsgSearchResults(null); } }}
                      placeholder="Search messages…"
                      className="h-9 rounded-full bg-muted/50 border-0 text-sm placeholder:text-muted-foreground/60 focus-visible:ring-1 focus-visible:ring-[hsl(var(--accent))]/30"
                      autoFocus
                      data-testid="chat-search-input"
                    />
                    <button
                      type="button"
                      onClick={() => { setMsgSearch(""); setMsgSearchResults(null); }}
                      className="shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      aria-label="Close search"
                      data-testid="chat-search-close"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  {/* Results list */}
                  {msgSearchBusy && (
                    <div className="px-4 pb-3 text-sm text-muted-foreground">Searching…</div>
                  )}
                  {!msgSearchBusy && msgSearch.trim().length >= 2 && msgSearchResults.length === 0 && (
                    <div className="px-4 pb-3 text-sm text-muted-foreground" data-testid="chat-search-empty">No messages found</div>
                  )}
                  {!msgSearchBusy && msgSearchResults.length > 0 && (
                    <div className="max-h-[320px] overflow-y-auto px-2 pb-2" data-testid="chat-search-results">
                      {msgSearchResults.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => handleSearchResultClick(r)}
                          className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-muted/60 transition-colors flex items-start gap-3"
                          data-testid={`chat-search-result-${r.id}`}
                        >
                          <Avatar className="h-8 w-8 shrink-0 mt-0.5">
                            <AvatarFallback className={`${avatarColor(r.sender_name)} text-white text-[10px] font-bold`}>
                              {initials(r.sender_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold truncate">{r.sender_name}</span>
                              {r.room_name && r.room_id !== activeId && (
                                <span className="text-[10px] text-muted-foreground bg-muted rounded-full px-2 py-0.5 shrink-0">
                                  {r.room_name}
                                </span>
                              )}
                            </div>
                            <div className="text-[13px] text-muted-foreground line-clamp-2 break-words mt-0.5">
                              {r.text || (r.image_url ? "📷 Image" : "")}
                            </div>
                            <div className="text-[10px] text-muted-foreground/50 mt-0.5">
                              {r.created_at ? fmtDateSeparator(r.created_at) + " · " + fmtTime(r.created_at) : ""}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Pinned messages banner */}
              {pinnedMessages.length > 0 && (
                <div className="shrink-0 border-b border-border" data-testid="chat-pinned-banner">
                  <button
                    type="button"
                    onClick={() => setShowPinnedPanel((v) => !v)}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-left hover:bg-muted/40 transition-colors"
                    data-testid="chat-pinned-toggle"
                  >
                    <Pin className="h-4 w-4 text-[hsl(var(--accent))] shrink-0 fill-[hsl(var(--accent))]" />
                    <div className="min-w-0 flex-1">
                      <span className="text-[12px] font-semibold text-[hsl(var(--accent))]">
                        {pinnedMessages.length} pinned message{pinnedMessages.length !== 1 ? "s" : ""}
                      </span>
                      {!showPinnedPanel && pinnedMessages[0] && (
                        <span className="text-[12px] text-muted-foreground ml-2 truncate">
                          — {pinnedMessages[0].sender_name}: {(pinnedMessages[0].text || "📷 Image").slice(0, 60)}
                        </span>
                      )}
                    </div>
                    <ChevronLeft className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${showPinnedPanel ? "-rotate-90" : "rotate-0"}`} />
                  </button>
                  {showPinnedPanel && (
                    <div className="max-h-[240px] overflow-y-auto px-2 pb-2 bg-muted/20" data-testid="chat-pinned-list">
                      {pinnedMessages.map((p) => (
                        <div
                          key={p.id}
                          className="flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-muted/60 transition-colors group/pin"
                        >
                          <button
                            type="button"
                            onClick={() => { setShowPinnedPanel(false); scrollToMessage(p.id); }}
                            className="flex-1 min-w-0 text-left"
                            data-testid={`chat-pinned-item-${p.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-[12px] font-semibold truncate">{p.sender_name}</span>
                              <span className="text-[10px] text-muted-foreground/50">{fmtTime(p.created_at)}</span>
                            </div>
                            <div className="text-[13px] text-muted-foreground line-clamp-2 break-words mt-0.5">
                              {p.text || (p.image_url ? "📷 Image" : "")}
                            </div>
                          </button>
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => unpinMessage(p.id)}
                              className="shrink-0 h-6 w-6 rounded-full opacity-0 group-hover/pin:opacity-100 focus:opacity-100 hover:bg-muted inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-all"
                              aria-label="Unpin"
                              data-testid={`chat-unpin-${p.id}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Media gallery panel */}
              {mediaGallery !== null && (
                <div className="flex-1 flex flex-col overflow-hidden" data-testid="chat-media-gallery">
                  {/* Gallery header */}
                  <div className="shrink-0 flex items-center gap-3 px-4 py-3 border-b border-border bg-background/95 backdrop-blur-sm">
                    <Images className="h-4.5 w-4.5 text-[hsl(var(--accent))] shrink-0" />
                    <span className="text-sm font-semibold flex-1">Shared Media</span>
                    <button
                      type="button"
                      onClick={() => setMediaGallery(null)}
                      className="shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      aria-label="Close media gallery"
                      data-testid="chat-media-close"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  {/* Gallery content */}
                  <div className="flex-1 overflow-y-auto p-3">
                    {mediaLoading && (
                      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">Loading media…</div>
                    )}
                    {!mediaLoading && mediaGallery.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="chat-media-empty">
                        <Images className="h-10 w-10 text-muted-foreground/30 mb-3" />
                        <div className="text-sm text-muted-foreground">No shared images yet</div>
                        <div className="text-[12px] text-muted-foreground/60 mt-1">Images shared in this room will appear here</div>
                      </div>
                    )}
                    {!mediaLoading && mediaGallery.length > 0 && (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5" data-testid="chat-media-grid">
                        {mediaGallery.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => setLightboxUrl(item.image_url)}
                            className="relative aspect-square rounded-lg overflow-hidden bg-muted/30 group hover:opacity-90 transition-opacity focus:ring-2 focus:ring-[hsl(var(--accent))]/50 focus:ring-offset-1 focus:ring-offset-background"
                            data-testid={`chat-media-item-${item.id}`}
                          >
                            <img
                              src={item.image_url}
                              alt={item.text || "Shared image"}
                              className="absolute inset-0 w-full h-full object-cover"
                              loading="lazy"
                            />
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-1.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="text-[10px] text-white font-medium truncate">{item.sender_name}</div>
                              <div className="text-[9px] text-white/70">{fmtDateSeparator(item.created_at)}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Lightbox overlay */}
              {lightboxUrl && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
                  onClick={() => setLightboxUrl(null)}
                  data-testid="chat-media-lightbox"
                >
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setLightboxUrl(null); }}
                    className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition-colors z-10"
                    aria-label="Close preview"
                    data-testid="chat-media-lightbox-close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                  <img
                    src={lightboxUrl}
                    alt="Full size"
                    className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}

              {/* Messages area — hidden when media gallery is open */}
              {mediaGallery === null && (
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4" data-testid="chat-messages-area">
                {messageGroups.map((group, gi) => {
                  if (group.isSystem) {
                    const m = group.messages[0];
                    return (
                      <div key={`sys-${gi}`} className="flex justify-center my-3">
                        <span className="text-[11px] text-muted-foreground/60 font-medium">{m.text}</span>
                      </div>
                    );
                  }

                  // Date separator before first message of group if different day from previous group
                  let dateSep = null;
                  if (gi > 0) {
                    const prevGroup = messageGroups[gi - 1];
                    const prevLast = prevGroup.messages[prevGroup.messages.length - 1];
                    const curFirst = group.messages[0];
                    if (prevLast.created_at && curFirst.created_at) {
                      const d1 = new Date(prevLast.created_at).toDateString();
                      const d2 = new Date(curFirst.created_at).toDateString();
                      if (d1 !== d2) {
                        dateSep = (
                          <div className="flex justify-center my-4">
                            <span className="text-[12px] text-muted-foreground/70 font-medium tracking-wide">{fmtDateSeparator(curFirst.created_at)}</span>
                          </div>
                        );
                      }
                    }
                  } else if (group.messages[0]?.created_at) {
                    dateSep = (
                      <div className="flex justify-center my-4">
                        <span className="text-[12px] text-muted-foreground/70 font-medium tracking-wide">{fmtDateSeparator(group.messages[0].created_at)}</span>
                      </div>
                    );
                  }

                  const mine = group.sender_id === user?.id;
                  return (
                    <React.Fragment key={`g-${gi}`}>
                      {dateSep}
                      {group.messages.map((m, mi) => (
                        <MessageBubble
                          key={m.id || `${gi}-${mi}`}
                          msg={m}
                          mine={mine}
                          isFirst={mi === 0}
                          isLast={mi === group.messages.length - 1}
                          userId={user?.id}
                          onReply={startReply}
                          onReaction={toggleReaction}
                          onPin={(msgId, alreadyPinned) => alreadyPinned ? unpinMessage(msgId) : pinMessage(msgId)}
                          onUnsend={(msgId) => unsendMessage(msgId)}
                          msgRefs={msgRefs}
                          scrollToMessage={scrollToMessage}
                          highlightId={highlightId}
                          seenUsers={m.id ? (seenByMessage[m.id] || null) : null}
                          isPinned={!!m.id && pinnedIds.has(m.id)}
                          isAdmin={isAdmin}
                        />
                      ))}
                    </React.Fragment>
                  );
                })}
                <div ref={endRef} />
              </div>
              )}

              {/* Typing indicator */}
              {mediaGallery === null && typingNames && (
                <div className="shrink-0 px-4 sm:px-6 pb-1 flex items-center gap-2" data-testid="chat-typing-indicator">
                  <div className="flex items-center gap-[3px] h-5">
                    <span className="w-[6px] h-[6px] rounded-full bg-muted-foreground/50 animate-[typingDot_1.4s_ease-in-out_infinite]" style={{ animationDelay: "0s" }} />
                    <span className="w-[6px] h-[6px] rounded-full bg-muted-foreground/50 animate-[typingDot_1.4s_ease-in-out_infinite]" style={{ animationDelay: "0.2s" }} />
                    <span className="w-[6px] h-[6px] rounded-full bg-muted-foreground/50 animate-[typingDot_1.4s_ease-in-out_infinite]" style={{ animationDelay: "0.4s" }} />
                  </div>
                  <span className="text-[12px] text-muted-foreground/70 font-medium">{typingNames}</span>
                </div>
              )}

              {/* Reply banner */}
              {mediaGallery === null && replyTo && (
                <div className="shrink-0 border-t border-border bg-muted/30 px-4 py-2 flex items-stretch gap-2" data-testid="chat-replying-banner">
                  <div className="w-1 rounded-full bg-[hsl(var(--accent))]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-semibold text-[hsl(var(--accent))]">Replying to {replyTo.sender_name || "member"}</div>
                    <div className="text-[12px] text-muted-foreground line-clamp-1 break-words">{replyTo.text || "(message)"}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setReplyTo(null)}
                    className="h-7 w-7 rounded-full hover:bg-muted inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
                    aria-label="Cancel reply"
                    data-testid="chat-cancel-reply"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              {/* Pending image preview */}
              {mediaGallery === null && pendingImage && (
                <div className="shrink-0 border-t border-border bg-muted/20 px-4 py-2 flex items-start gap-3" data-testid="chat-pending-image">
                  <div className="relative rounded-xl overflow-hidden border border-border bg-background">
                    {pendingImage.compressing ? (
                      <div className="h-20 w-20 flex items-center justify-center">
                        <div className="flex flex-col items-center gap-1.5">
                          <div className="h-5 w-5 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin" />
                          <span className="text-[9px] text-muted-foreground font-medium">Compressing</span>
                        </div>
                      </div>
                    ) : (
                      <img src={pendingImage.dataUrl} alt="Preview" className="h-20 w-auto max-w-[160px] object-contain" />
                    )}
                    <button
                      type="button"
                      onClick={() => setPendingImage(null)}
                      className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white inline-flex items-center justify-center hover:bg-black/80 transition-colors"
                      aria-label="Remove image"
                      data-testid="chat-remove-image"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                  <span className="text-[11px] text-muted-foreground mt-1 truncate max-w-[160px]">{pendingImage.name}</span>
                </div>
              )}

              {/* Composer */}
              {mediaGallery === null && (
              <div className="shrink-0 border-t border-border px-3 sm:px-4 py-3 flex items-center gap-2 bg-background">
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,.png"
                  className="hidden"
                  onChange={handleImageSelect}
                  data-testid="chat-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center justify-center shrink-0 h-10 w-10 rounded-full text-muted-foreground hover:text-[hsl(var(--accent))] hover:bg-muted transition-colors"
                  aria-label="Attach image"
                  data-testid="chat-attach-image"
                >
                  <ImagePlus className="h-5 w-5" />
                </button>
                <EmojiPicker
                  onSelect={appendEmoji}
                  testid="chat-emoji"
                  triggerClassName="inline-flex items-center justify-center shrink-0 h-10 w-10 rounded-full text-muted-foreground hover:text-[hsl(var(--accent))] hover:bg-muted transition-colors"
                />
                <Input
                  ref={inputRef}
                  value={text}
                  onChange={(e) => { setText(e.target.value); emitTyping(); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                    if (e.key === "Escape" && replyTo) { setReplyTo(null); }
                  }}
                  placeholder={replyTo ? `Reply to ${replyTo.sender_name || "member"}…` : "Aa"}
                  disabled={!activeId}
                  className="h-10 rounded-full bg-muted/50 border-0 text-sm placeholder:text-muted-foreground/60 focus-visible:ring-1 focus-visible:ring-[hsl(var(--accent))]/30"
                  data-testid="chat-input"
                />
                <Button
                  onClick={send}
                  disabled={!activeId || (!text.trim() && !pendingImage) || pendingImage?.compressing}
                  className="h-10 w-10 rounded-full p-0 shrink-0 shadow-none"
                  style={{ background: (text.trim() || pendingImage) ? "linear-gradient(135deg, #4169E1 0%, #3058c8 100%)" : undefined }}
                  data-testid="chat-send"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
